import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";

const DynamicForm = ({
  fields,
  onSubmit,
  title,
  subtitle,
  initialValues,
  onFieldChange,
}) => {
  const [dependentOptions, setDependentOptions] = useState({});

  const validationSchema = yup.object().shape(
    fields.reduce((acc, field) => {
      let schema;
      switch (field.type) {
        case "text":
          schema = yup.string().nullable();
          break;
        case "number":
          schema = yup.number().nullable();
          break;
        case "date":
          schema = yup.date().nullable();
          break;
        case "select":
          schema = yup.string();
          break;
        case "multiselect":
          schema = yup.array().nullable();
          break;
        default:
          schema = yup.string().nullable();
      }
      if (field.required) {
        schema = schema.required(`${field.label} is required`);
      }
      acc[field.name] = schema;
      return acc;
    }, {})
  );

  const handleFieldChangeInternal = async (
    setFieldValue,
    field,
    value,
    values
  ) => {
    setFieldValue(field.name, value);

    if (field.parent === "main") {
      const options = await onFieldChange(field, value);
      setDependentOptions(options || []);
    }
  };
const shouldDisableField = (field, values) => {
  // General condition to disable fields based on any condition
  if (field.disableCondition) {
    const disableField = fields.find(
      (f) => f.name === field.disableCondition.fieldName
    );
    return values[disableField.name] === field.disableCondition.value;
  }
  return false;
};
  return (
    <Box m="20px">
      <Header title={title} subtitle={subtitle} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              {fields.map((field) => {
                const isDependent =
                  field.dependentOn && !values[field.dependentOn];
                const isDisabled = shouldDisableField(field, values);
                return (
                  <Box key={field.name} sx={{ gridColumn: field.gridColumn }}>
                    {field.type === "select" || field.type === "multiselect" ? (
                      <Autocomplete
                        disabled={isDependent}
                        multiple={field.type === "multiselect"}
                        options={
                          field.parent === "dependent"
                            ? dependentOptions || []
                            : field.options || []
                        }
                        getOptionLabel={(option) =>
                          option ? option.label : ""
                        }
                        onChange={(event, newValue) => {
                          handleFieldChangeInternal(
                            setFieldValue,
                            field,
                            field.type === "multiselect"
                              ? newValue.map((item) => item.value)
                              : newValue
                              ? newValue.value
                              : "",
                            values
                          );
                        }}
                        onBlur={handleBlur}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={field.label}
                            variant="filled"
                            name={field.name}
                            error={
                              !!touched[field.name] && !!errors[field.name]
                            }
                            helperText={
                              touched[field.name] && errors[field.name]
                            }
                          />
                        )}
                        value={
                          Array.isArray(field.options)
                            ? field.type === "multiselect"
                              ? field.options.filter(
                                  (option) =>
                                    Array.isArray(values[field.name]) &&
                                    values[field.name]?.includes(option.value)
                                )
                              : field.options.find(
                                  (option) =>
                                    option.value === values[field.name]
                                ) || null
                            : null
                        }
                      />
                    ) : field.type === "checkbox" ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values[field.name]}
                            onChange={(event) => {
                              handleFieldChangeInternal(
                                setFieldValue,
                                field,
                                event.target.checked,
                                values
                              );
                            }}
                            name={field.name}
                          />
                        }
                        label={field.label}
                      />
                    ) : (
                      <TextField
                        disabled={isDisabled}
                        fullWidth
                        variant="filled"
                        type={field.type}
                        label={field.label}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values[field.name]}
                        name={field.name}
                        error={!!touched[field.name] && !!errors[field.name]}
                        helperText={touched[field.name] && errors[field.name]}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </Box>
            {isSubmitting && <CircularProgress />}
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default DynamicForm;
